import type { AnimeRank, Rarity } from '@anidex/shared/economy';
import { useTranslation } from 'react-i18next';
import { rarityLabel } from '../services/format';

const MAX_PIPS = 10;

export function RarityBadge({ rarity }: { rarity: Rarity }) {
  return <span className={`rarity-badge rarity-${rarity}`}>{rarityLabel(rarity)}</span>;
}

function rankLabel(rank: AnimeRank): string {
  return rank === 'DEUS-EX' ? 'Deus-Ex' : rank;
}

export function RankBadge({ rank, size = 'md' }: { rank: AnimeRank; size?: 'sm' | 'md' | 'lg' }) {
  const { t } = useTranslation();
  const label = rankLabel(rank);
  return (
    <span className={`rank-badge rank-badge--${size} rank-${rank.toLowerCase()}`} aria-label={t('rank.aria', { rank: label })}>
      {label}
    </span>
  );
}

export function ProgressBar({ value, max, label, thin = false }: { value: number; max: number; label?: string; thin?: boolean }) {
  const percent = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <span
      className={`progress${thin ? ' progress--thin' : ''}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-label={label}
    >
      <span className="progress__fill" style={{ width: `${percent}%` }} />
    </span>
  );
}

export function ProgressMeter({ value, max, label }: { value: number; max: number; label?: string }) {
  if (max < 1 || max > MAX_PIPS) return <ProgressBar value={value} max={max} label={label} />;
  return (
    <span className="pips" role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-label={label}>
      {Array.from({ length: max }, (_, index) => (
        <span key={index} className={index < value ? 'is-on' : undefined} />
      ))}
    </span>
  );
}
